import React, { useEffect, useMemo, useRef, useState } from "react";
import { filterFn_includesString, flexRender, useTable } from "@tanstack/react-table";
import { dataTableFeatures, facetFilterFn } from "./tableFeatures";
import ColumnVisibilityMenu from "./ColumnVisibilityMenu";
import FacetFilter from "./FacetFilter";
import TablePagination from "./TablePagination";
import { SkeletonTable } from "../Skeleton";

/**
 * The listing table for this app. One component owns the toolbar, the table
 * and the footer so every screen gets the same sorting, filtering, column
 * visibility, selection and pagination behaviour.
 *
 * It runs in two modes, and the mode is a property of the endpoint, not a
 * preference:
 *
 *   client  (default)  `data` is the whole dataset. Search, faceted filters,
 *                      sorting and pagination all run in the browser, so the
 *                      facet counts and the row total are true.
 *   server  (`pagination.manual`) `data` is one page. The table never filters
 *                      or sorts what it cannot see: search is handed to the
 *                      caller (debounced) and page changes are reported
 *                      through `pagination.onChange`.
 *
 * Props
 *   columns      TanStack column defs. `meta.width` sets the column width,
 *                `meta.className` is added to every cell, `meta.label` names
 *                the column in the View menu when `header` is not a string.
 *   data         array of rows (whole dataset, or one page in server mode)
 *   isLoading    swaps the body for a page of skeleton rows
 *   getRowId     stable row id; strongly recommended when selection is on
 *   search       `{ value, onChange, placeholder, debounce }` -> server search
 *                `{ columns: ["name", "email"], placeholder }` -> client search
 *   facets       `[{ columnId, label }]` faceted checkbox filters (client mode)
 *   hiddenColumns ids hidden on first render — a column that exists to be
 *                faceted or searched rather than read. The View menu still
 *                lists it, so the user can pull it into the table.
 *   filters      node rendered in the toolbar (the react-select server filters)
 *   actions      node rendered at the toolbar's right edge
 *   bulkActions  `(selectedRows, table) => node`; enables the selection column
 *   sorting      omitted -> client sorting; `false` -> off;
 *                `{ value, onChange }` -> caller/server owns the sort
 *   pagination   `false` -> single page; `{ pageSize, pageSizeOptions }`;
 *                `{ manual: true, pageIndex, pageSize, pageCount, rowCount,
 *                   onChange, pageSizeOptions }`
 *   rowTone      `(row) => "success" | "attention" | "danger" | "muted" | null`
 *                washes the whole row in that status tint and marks its first
 *                cell with a 3px stripe. Decorative: the status still has to
 *                be readable in a cell, because a tint is not a label
 *   emptyText    empty-state copy
 */
const DataTable = ({
  columns,
  data,
  isLoading = false,
  getRowId,
  search,
  facets,
  hiddenColumns,
  filters,
  actions,
  bulkActions,
  sorting,
  pagination,
  rowTone,
  emptyText = "No record found",
  className = "",
}) => {
  const rows = useMemo(() => data || [], [data]);
  const facetList = useMemo(() => facets || [], [facets]);
  const selectable = typeof bulkActions === "function";

  const paging = pagination === false ? null : pagination || {};
  const manual = Boolean(paging && paging.manual);
  const pageSizeOptions = (paging && paging.pageSizeOptions) || [10, 25, 50, 100];
  const serverSearch = Boolean(search && search.onChange);

  const tableColumns = useMemo(() => {
    const faceted = new Set(facetList.map((facet) => facet.columnId));
    const prepared = (columns || []).map((column) => {
      const id = column.id || column.accessorKey;
      if (!faceted.has(id) || column.filterFn) return column;
      return { ...column, filterFn: facetFilterFn };
    });
    return selectable ? [selectionColumn, ...prepared] : prepared;
  }, [columns, facetList, selectable]);

  const searchColumns = (!serverSearch && search && search.columns) || null;

  // `initialState` is read once, so this only has to be stable enough to
  // survive the first render.
  const initialVisibility = useMemo(() => {
    if (!hiddenColumns || !hiddenColumns.length) return null;
    return hiddenColumns.reduce(
      (state, id) => ({ ...state, [id]: false }),
      {}
    );
  }, [hiddenColumns]);

  // A key set to `undefined` still overrides the feature default, and every
  // state slice is written through `on<Slice>Change`: passing
  // `onPaginationChange: undefined` silently makes that slice read-only. So the
  // controlled options are spread in only when the caller really owns them.
  const serverPagination = manual
    ? {
        manualPagination: true,
        ...(typeof paging.rowCount === "number"
          ? { rowCount: paging.rowCount }
          : { pageCount: paging.pageCount ?? -1 }),
        onPaginationChange: (updater) =>
          paging.onChange(
            resolveUpdater(updater, {
              pageIndex: paging.pageIndex,
              pageSize: paging.pageSize,
            })
          ),
      }
    : null;
  const serverSorting =
    sorting && sorting.onChange
      ? {
          manualSorting: true,
          onSortingChange: (updater) =>
            sorting.onChange(resolveUpdater(updater, sorting.value || [])),
        }
      : null;
  // `pagination === false` means one page carrying every row. Seeding that
  // size through `initialState` does not work: it is read once, so a table
  // whose rows arrive from an API keeps the size it had while it was still
  // empty - one row, with no footer to escape it. The size is controlled
  // instead and follows the data. Nothing in the UI writes this slice, so it
  // needs no `onPaginationChange`.
  const staticPagination = paging
    ? null
    : { pageIndex: 0, pageSize: Math.max(rows.length, 1) };
  const controlledState =
    serverPagination || staticPagination || serverSorting
      ? {
          state: {
            ...(serverPagination
              ? {
                  pagination: {
                    pageIndex: paging.pageIndex,
                    pageSize: paging.pageSize,
                  },
                }
              : {}),
            ...(staticPagination ? { pagination: staticPagination } : {}),
            ...(serverSorting ? { sorting: sorting.value || [] } : {}),
          },
        }
      : null;

  const table = useTable({
    features: dataTableFeatures,
    columns: tableColumns,
    data: rows,
    getRowId,
    enableSortingRemoval: false,
    // TanStack sorts numeric columns high-to-low on the first click and text
    // columns low-to-high. One direction for every column is less surprising.
    sortDescFirst: false,
    enableSorting: sorting !== false,
    enableRowSelection: selectable,
    enableGlobalFilter: Boolean(searchColumns),
    globalFilterFn: filterFn_includesString,
    getColumnCanGlobalFilter: (column) =>
      Boolean(searchColumns && searchColumns.includes(column.id)),
    ...(serverPagination || {}),
    ...(serverSorting || {}),
    ...(controlledState || {}),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: paging ? paging.pageSize || 10 : rows.length || 1, // unpaginated: overridden by `staticPagination` above
      },
      ...(initialVisibility ? { columnVisibility: initialVisibility } : {}),
    },
  });

  const [draft, setDraft] = useDebouncedSearch(
    serverSearch ? search.value : table.state.globalFilter,
    serverSearch ? search.onChange : table.setGlobalFilter,
    serverSearch ? search.debounce ?? 400 : 0
  );

  const selectedRows = selectable ? table.getSelectedRowModel().rows : [];
  const visibleRows = table.getRowModel().rows;
  const total = manual
    ? typeof paging.rowCount === "number"
      ? paging.rowCount
      : null
    : table.getRowCount();
  // How many placeholder rows to draw: the page size the next response will
  // fill, so the table is already that tall. Capped, because a 100-row page
  // of bars is a wall, and floored at the rows already on screen so a refetch
  // never shrinks the surface mid-flight.
  const skeletonRowCount = Math.min(
    Math.max(table.state.pagination?.pageSize || 10, visibleRows.length || 0, 3),
    12
  );
  // The labelled server filters get their own bar: four react-selects beside a
  // search box and a View menu is a scrum, not a toolbar.
  // The View menu lives in this row too, so it stays unless there is genuinely
  // nothing to show: no search, no facets, no actions, nothing hideable.
  const showControls = Boolean(
    search ||
      facetList.length ||
      actions ||
      selectable ||
      table.getAllColumns().some((column) => column.getCanHide())
  );
  // With nothing for the toolbar's left side, its right side (View, bulk
  // actions) joins the filter bar instead of floating alone on a row of its
  // own, bottom-aligned so it lines up with the dropdowns, not their labels.
  const sideBesideFilters = Boolean(filters) && !search && !facetList.length;

  const toolbarSide = (
    <div className="dt-toolbar-side">
      {selectedRows.length > 0 ? (
        <span className="dt-selected">
          {selectedRows.length} selected
        </span>
      ) : null}
      {selectedRows.length > 0 ? bulkActions(selectedRows, table) : null}
      <ColumnVisibilityMenu table={table} />
      {actions}
    </div>
  );

  return (
    <div className={("dt " + className).trim()}>
      {filters && sideBesideFilters ? (
        <div className="dt-filters dt-filters-row">
          <div className="dt-filters-main">{filters}</div>
          {toolbarSide}
        </div>
      ) : filters ? (
        <div className="dt-filters">{filters}</div>
      ) : null}
      {showControls && !sideBesideFilters ? (
        <div className="dt-toolbar">
          <div className="dt-toolbar-main">
            {search ? (
              <div className="dt-search">
                <i className="fa fa-search dt-search-icon" aria-hidden="true"></i>
                <input
                  type="text"
                  className="form-control dt-search-input"
                  aria-label={search.placeholder || "Search"}
                  placeholder={search.placeholder || "Search..."}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                />
                {draft ? (
                  <button
                    type="button"
                    className="dt-search-clear"
                    aria-label="Clear search"
                    onClick={() => setDraft("")}
                  >
                    <i className="fa fa-times-circle" aria-hidden="true"></i>
                  </button>
                ) : null}
              </div>
            ) : null}
            {facetList.map((facet) => (
              <FacetFilter
                key={facet.columnId}
                column={table.getColumn(facet.columnId)}
                label={facet.label}
              />
            ))}
          </div>
          {toolbarSide}
        </div>
      ) : null}

      <div className="dt-surface">
        <div className="dt-scroll">
          {/* Placeholder rows, not a spinner over a dimmed table: the surface
              is already the height one page of data will make it, so nothing
              below it moves when the response lands - and the skeleton rows
              then hand over to the real ones one by one. */}
          <SkeletonTable
            loading={isLoading}
            className="table table-sm dt-table"
            rows={skeletonRowCount}
            columns={table.getVisibleFlatColumns().length}
            head={
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta || {};
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        className={
                          "site-color" +
                          (meta.className ? " " + meta.className : "") +
                          (canSort ? " dt-sortable" : "")
                        }
                        style={meta.width ? { width: meta.width } : undefined}
                        aria-sort={
                          sorted === "asc"
                            ? "ascending"
                            : sorted === "desc"
                            ? "descending"
                            : undefined
                        }
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        onKeyDown={
                          canSort
                            ? (event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  header.column.getToggleSortingHandler()(event);
                                }
                              }
                            : undefined
                        }
                        tabIndex={canSort ? 0 : undefined}
                      >
                        <span className="dt-th">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {canSort ? (
                            <i
                              className={
                                "fa dt-sort-icon " +
                                (sorted === "asc"
                                  ? "fa-sort-asc is-on"
                                  : sorted === "desc"
                                  ? "fa-sort-desc is-on"
                                  : "fa-sort")
                              }
                              aria-hidden="true"
                            ></i>
                          ) : null}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            }
          >
              {visibleRows.length === 0 ? (
                <tr>
                  <td
                    className="dt-empty"
                    colSpan={table.getVisibleFlatColumns().length}
                  >
                    {emptyText}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <tr key={row.id} className={rowClassName(row, rowTone)}>
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta || {};
                      return (
                        <td
                          key={cell.id}
                          className={meta.className}
                          data-label={
                            typeof cell.column.columnDef.header === "string"
                              ? cell.column.columnDef.header
                              : undefined
                          }
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
          </SkeletonTable>
        </div>
      </div>

      {paging ? (
        <TablePagination
          table={table}
          pageSizeOptions={pageSizeOptions}
          total={total}
        />
      ) : null}
    </div>
  );
};

/**
 * The two things a row's class carries: the caller's status tint and the
 * selection state. An unstyled tone name is a no-op in CSS, so a status the
 * theme does not know about costs nothing.
 */
const rowClassName = (row, rowTone) => {
  const tone = rowTone ? rowTone(row) : null;
  const names = [];
  if (tone) names.push("dt-tone-" + tone);
  if (row.getIsSelected()) names.push("dt-row-selected");
  return names.length ? names.join(" ") : undefined;
};

/** Row-selection column, prepended whenever `bulkActions` is supplied. */
const selectionColumn = {
  id: "select",
  enableHiding: false,
  enableSorting: false,
  meta: { width: "40px", className: "dt-select-cell" },
  header: ({ table }) => (
    <input
      type="checkbox"
      className="form-check-input"
      aria-label="Select all rows on this page"
      checked={table.getIsAllPageRowsSelected()}
      ref={(node) => {
        if (node) node.indeterminate = table.getIsSomePageRowsSelected();
      }}
      onChange={(event) => table.toggleAllPageRowsSelected(event.target.checked)}
    />
  ),
  cell: ({ row }) => (
    <input
      type="checkbox"
      className="form-check-input"
      aria-label="Select row"
      checked={row.getIsSelected()}
      disabled={!row.getCanSelect()}
      onChange={(event) => row.toggleSelected(event.target.checked)}
    />
  ),
};

const resolveUpdater = (updater, current) =>
  typeof updater === "function" ? updater(current) : updater;

/**
 * Keeps the search box responsive while the committed value lands later.
 * In server mode every commit is a request, so `delay` throttles the caller;
 * in client mode the table filters immediately (`delay === 0`).
 */
const useDebouncedSearch = (value, commit, delay) => {
  const [draft, setDraft] = useState(value || "");
  const committed = useRef(value || "");
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    const next = value || "";
    if (next !== committed.current) {
      committed.current = next;
      setDraft(next);
    }
  }, [value]);

  useEffect(() => {
    if (draft === committed.current) return undefined;
    if (delay === 0) {
      committed.current = draft;
      commitRef.current(draft);
      return undefined;
    }
    const timer = setTimeout(() => {
      committed.current = draft;
      commitRef.current(draft);
    }, delay);
    return () => clearTimeout(timer);
  }, [draft, delay]);

  return [draft, setDraft];
};

export default DataTable;
